<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { computed } from 'vue';
import format from 'date-fns/format';
import ReportLineChart from '../ReportLineChart.vue';
import ReportCard from '../ReportCard.vue';

const props = defineProps({
  analytics: {
    type: Object,
    default: () => ({}),
  },
  rankings: {
    type: Array,
    default: () => [],
  },
  dailySummary: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const pauseAnalytics = computed(() => props.analytics.pause_analytics || {});
const availability = computed(() => props.analytics.availability_changes || {});
const maxRanking = computed(() =>
  Math.max(...props.rankings.map(row => row.total_pause_sessions || 0), 1)
);

const formatDuration = minutes => {
  if (!minutes) return '0m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${Math.round(minutes % 60)}m`;
};

const summaryCards = computed(() => [
  {
    label: 'Sessões de pausa',
    value: pauseAnalytics.value.total_pause_sessions || 0,
    icon: 'status',
  },
  {
    label: 'Tempo total em pausa',
    value: formatDuration(pauseAnalytics.value.total_pause_duration_minutes),
    icon: 'calendar-clock',
  },
  {
    label: 'Média por pausa',
    value: formatDuration(pauseAnalytics.value.average_pause_duration_minutes),
    icon: 'status',
  },
  {
    label: 'Mudanças de disponibilidade',
    value: availability.value.total_changes || 0,
    icon: 'arrow-swap',
  },
]);

const dataset = (label, data, color) => ({
  label,
  data,
  borderColor: color,
  backgroundColor: color,
});

const chartCollection = computed(() => ({
  labels: props.dailySummary.map(row => format(new Date(row.date), 'dd MMM')),
  datasets: [
    dataset(
      'Sessões de pausa',
      props.dailySummary.map(row => row.total_pause_sessions || 0),
      '#1f93ff'
    ),
    dataset(
      'Mudanças de disponibilidade',
      props.dailySummary.map(row => row.total_availability_changes || 0),
      '#14b8a6'
    ),
  ],
}));
</script>

<template>
  <div class="space-y-4">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <ReportCard
        v-for="card in summaryCards"
        :key="card.label"
        :is-loading="isLoading"
      >
        <div class="flex items-center gap-4">
          <span class="activity-icon">
            <fluent-icon :icon="card.icon" size="22" />
          </span>
          <div>
            <p class="mb-1 text-sm text-slate-500 dark:text-slate-400">
              {{ card.label }}
            </p>
            <strong class="text-3xl text-slate-900 dark:text-slate-50">{{
              card.value
            }}</strong>
          </div>
        </div>
      </ReportCard>
    </div>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <ReportCard class="xl:col-span-7" :is-loading="isLoading">
        <div class="mb-4">
          <h3
            class="mb-0 text-lg font-medium text-slate-900 dark:text-slate-50"
          >
            Evolução diária
          </h3>
        </div>
        <div v-if="props.dailySummary.length" class="w-full h-64">
          <ReportLineChart :collection="chartCollection" />
        </div>
        <div
          v-else
          class="flex items-center justify-center h-64 text-sm text-slate-500 dark:text-slate-400"
        >
          Sem atividade no período.
        </div>
      </ReportCard>

      <ReportCard class="xl:col-span-5" :is-loading="isLoading">
        <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
          Ranking de pausas
        </h3>
        <div class="space-y-4">
          <div
            v-for="row in rankings.slice(0, 8)"
            :key="row.user_id"
            class="grid grid-cols-[1fr_1fr_2rem] items-center gap-3 text-sm"
          >
            <span class="truncate text-slate-700 dark:text-slate-200">
              {{ row.rank }}. {{ row.user_name }}
            </span>
            <span
              class="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
            >
              <span
                class="block h-full rounded-full bg-woot-500"
                :style="{
                  width: `${((row.total_pause_sessions || 0) / maxRanking) * 100}%`,
                }"
              />
            </span>
            <strong class="text-right text-slate-800 dark:text-slate-100">{{
              row.total_pause_sessions
            }}</strong>
          </div>
          <p
            v-if="!rankings.length"
            class="mb-0 text-sm text-slate-500 dark:text-slate-400"
          >
            Sem ranking no período.
          </p>
        </div>
      </ReportCard>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.activity-icon {
  @apply flex items-center justify-center flex-shrink-0 w-11 h-11 rounded-full text-woot-500 bg-woot-50 dark:bg-woot-900/40;
}
</style>
